import type { Aircraft } from '@/types';

// ---------------------------------------------------------------------------
// Aircraft Tracking
// In dev: GET /api/aircraft  → Vite middleware aggregates adsb.lol server-side
//         (no CORS, no HTTP/2 proxy issues, 60s cache, ~2000 aircraft)
// In prod: GET /api/aircraft → real backend (or falls back to OpenSky)
// ---------------------------------------------------------------------------

interface AircraftApiResponse {
  aircraft: Aircraft[];
  count: number;
}

export async function fetchAircraft(): Promise<Aircraft[]> {
  // Primary: server-side aggregated endpoint (dev middleware or prod backend)
  if (import.meta.env.DEV) {
    try {
      const res = await fetch('/api/aircraft', {
        signal: AbortSignal.timeout(30_000),
      });
      if (res.ok) {
        const data: AircraftApiResponse = await res.json();
        if (data.aircraft?.length > 0) {
          console.log(`[Aircraft] /api/aircraft: ${data.count} aircraft`);
          return data.aircraft;
        }
      }
    } catch (err) {
      console.warn('[Aircraft] /api/aircraft failed:', err);
    }
  }

  // Fallback: OpenSky (prod or if dev endpoint fails)
  try {
    const aircraft = await fetchFromOpenSky();
    if (aircraft.length > 0) {
      console.log(`[Aircraft] OpenSky fallback: ${aircraft.length} aircraft`);
      return aircraft;
    }
  } catch (err) {
    console.warn('[Aircraft] OpenSky also failed:', err);
  }

  console.warn('[Aircraft] Using simulated fallback');
  return getFallbackAircraft();
}

// ---------------------------------------------------------------------------
// OpenSky fallback
// ---------------------------------------------------------------------------

const _OS_USER = import.meta.env.VITE_OPENSKY_USER as string | undefined;
const _OS_PASS = import.meta.env.VITE_OPENSKY_PASS as string | undefined;
const HAS_OPENSKY_AUTH = Boolean(_OS_USER && _OS_PASS);

const OPENSKY_URL = import.meta.env.DEV
  ? '/opensky-proxy/api/states/all'
  : 'https://opensky-network.org/api/states/all';

const OPENSKY_HEADERS: Record<string, string> = {
  Accept: 'application/json',
  ...(!import.meta.env.DEV && HAS_OPENSKY_AUTH
    ? { Authorization: `Basic ${btoa(`${_OS_USER}:${_OS_PASS}`)}` }
    : {}),
};

type RawState = [
  string, string, string,
  number | null, number,
  number | null, number | null, number | null,
  boolean, number | null, number | null, number | null,
  null, number | null, string | null, boolean, number,
];

interface OpenSkyResponse { time: number; states: RawState[] | null; }

const MILITARY_PATTERNS = [
  /^(RCH|REACH|USAF|NAVY|ARMY|USMC|USCG|DUKE|JAKE|TOPAZ|ATLAS|BISON|BOXER)/i,
  /^(CNV|ROCKY|HAWK|EAGLE|FALCON|VIPER|RAPTOR|TITAN|GLOBE|TREK)/i,
  /^(RFR|RFF|RAF|AAR|GAF|GERM|FAF|SFRA)/i,
  /^(KAF|ROKAF|JASDF)/i,
];

async function fetchFromOpenSky(): Promise<Aircraft[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20_000);
  try {
    const res = await fetch(OPENSKY_URL, { signal: controller.signal, headers: OPENSKY_HEADERS });
    if (!res.ok) throw new Error(`OpenSky ${res.status}`);
    const data: OpenSkyResponse = await res.json();
    if (!data.states) return [];
    const result: Aircraft[] = [];
    for (const s of data.states) {
      const lon = s[5], lat = s[6];
      if (lon == null || lat == null || s[8]) continue;
      const id = s[0];
      const callsign = (s[1] ?? '').trim();
      result.push({
        id, callsign: callsign || id,
        country: s[2] ?? '',
        lat, lon,
        // OpenSky 는 m / m·s⁻¹ 로 주므로 ADS-B 표준 단위(피트·노트)로 변환
        altitudeFt: (s[7] ?? s[13] ?? 0) * 3.28084,
        speedKt: (s[9] ?? 0) * 1.94384,
        heading: s[10] ?? 0,
        onGround: false,
        isMilitary: id.toLowerCase().startsWith('ae') ||
          (callsign.length > 0 && MILITARY_PATTERNS.some(r => r.test(callsign))),
      });
      if (result.length >= 1500) break;
    }
    return result;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Simulated fallback
// ---------------------------------------------------------------------------

const FALLBACK: { lat: number; lon: number; heading: number; callsign: string; isMilitary: boolean }[] = [
  { lat: 52.1, lon: -20.0, heading: 280, callsign: 'BAW112',  isMilitary: false },
  { lat: 50.3, lon: -30.5, heading:  95, callsign: 'AAL101',  isMilitary: false },
  { lat: 48.0, lon:  10.0, heading: 200, callsign: 'DLH400',  isMilitary: false },
  { lat: 38.5, lon: 150.0, heading:  60, callsign: 'ANA809',  isMilitary: false },
  { lat: 35.2, lon: 160.0, heading:  45, callsign: 'KAL002',  isMilitary: false },
  { lat: 13.0, lon: 103.0, heading: 185, callsign: 'SIA321',  isMilitary: false },
  { lat: 25.3, lon:  50.0, heading: 270, callsign: 'UAE203',  isMilitary: false },
  { lat: 41.0, lon: -74.0, heading: 270, callsign: 'UAL328',  isMilitary: false },
  { lat: 33.5, lon: -90.0, heading:  45, callsign: 'DAL804',  isMilitary: false },
  { lat:-34.0, lon: 151.0, heading: 270, callsign: 'QFA12',   isMilitary: false },
  { lat: 38.0, lon:-120.0, heading: 180, callsign: 'REACH99', isMilitary: true  },
  { lat: 55.0, lon:  35.0, heading: 130, callsign: 'RFF112',  isMilitary: true  },
  { lat: 37.5, lon: 127.0, heading:  90, callsign: 'ROKAF1',  isMilitary: true  },
  { lat: 52.0, lon:  -1.5, heading: 200, callsign: 'RAF301',  isMilitary: true  },
];

function getFallbackAircraft(): Aircraft[] {
  const now = Date.now();
  return FALLBACK.map((r, i) => ({
    id: `fallback-${i}`,
    callsign: r.callsign,
    country: '',
    lat: r.lat + Math.sin(now / 100000 + i) * 0.5,
    lon: r.lon + Math.cos(now / 100000 + i) * 0.5,
    altitudeFt: 33000 + (i % 5) * 2000,
    speedKt: 430 + (i % 10) * 15,
    heading: r.heading,
    onGround: false,
    isMilitary: r.isMilitary,
  }));
}
